import React, { ComponentProps, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import moment from 'moment'
import Clickable from 'components/Common/Clickable'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { pluralizeToString } from 'lib/string/pluralize'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import SolidUsdCircleIcon from 'LuxKit/Icons/solid/SolidUsdCircleIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { isDateWithinAnyDiscountPeriod } from 'components/OfferPage/utils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'

const HeaderGroup = styled(Group)`
  position: relative;
`

const CellWrapper = styled(Clickable)`
  --transition-timing: 0.2s;

  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color var(--transition-timing);
  min-height: ${rem(72)};

  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.six},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.six},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.six} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.default.six} inset;

  &:not(.check-in):not(.check-out):not(:disabled):not(.other-availability):not(.is-complementary) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &:disabled, &.unselectable {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.check-in, &.check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.is-bundle {
    justify-content: space-between;
  }

  &.sold-out-background {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.is-complementary {
    --polygon-width: ${rem(16)};
    --polygon-base: 0;
    background-color: ${props => props.theme.palette.brand.primary.normal};

     /* The Bundle & Save Polygon */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 1px;
      width: 100%;
      height: 100%;
      z-index: 0;
      clip-path: polygon(0 0, calc(var(--polygon-base) + var(--polygon-width)) 0, var(--polygon-base) 100%, 0 100%);
      background-color: ${props => props.theme.palette.neutral.default.one};
      transform-origin: center;
    }

    &.complementary-check-in {
      position: relative;
      background-color: ${props => props.theme.palette.neutral.default.eight};

      &::before {
        right: 0;
        transform: scaleX(-1) scaleY(-1);
      }

      &.check-out {
        background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
      }
    }

    &.complementary-check-out {
      --polygon-base: 20%;
      position: relative;
      background-color: ${props => props.theme.palette.neutral.default.eight};

      &::before {
        transform: scaleX(1);
      }

      &.check-in {
        background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
      }
    }
  }
`

const PropertyNameContainer = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
`

const Main = styled.div`
  position: relative;
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  &.align-items-end {
    align-items: end;
  }
`

const BundleAndSaveAdditionalAmount = styled.div`
  text-align: right;
`

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  duration: number
  day: App.CalendarDay
  hasOtherAvailability: boolean
  style?: React.CSSProperties
  isRoomSelected?: boolean
  className?: string
  currentOfferId?: string
  bundleOfferId?: string
  flights?: boolean
  isBetweenDay?: boolean
  isComplementaryBundleCell?: boolean
  isComplementaryCheckIn?: boolean
  isComplementaryCheckOut?: boolean
  complementaryPropertyName?: string
  isIllegalBundleSelection?: boolean
  checkIn: string
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  hasPromo?: boolean
  isTacticalPromotion?: boolean
  onClick?: (day: App.CalendarDay) => void
  cheapestDay?: App.CalendarCheapestDay
  onInvalidBundleDateClick: () => void
  showUrgencyLabels?: boolean
  soldOutText?: string
  displayMemberPrice: boolean
  villasDiscounts?: Array<App.Discount>
  hideChildPrices?: boolean
  markCheapestPrice: boolean
  tier: App.LoyaltyTier
  canLoyaltyUpgrade?: boolean
}

function OfferCalendarDayCell(props: Props) {
  const {
    duration,
    day,
    hasOtherAvailability,
    isCheckIn,
    isCheckOut,
    style,
    isRoomSelected,
    className,
    flights,
    isBetweenDay,
    flightsOnly,
    enableCalendarRecommendations,
    currentOfferId,
    bundleOfferId,
    isComplementaryBundleCell = false,
    isComplementaryCheckIn = false,
    isComplementaryCheckOut = false,
    isIllegalBundleSelection = false,
    complementaryPropertyName,
    onClick,
    hasPromo = false,
    isTacticalPromotion = false,
    cheapestDay,
    onInvalidBundleDateClick,
    showUrgencyLabels,
    soldOutText,
    displayMemberPrice,
    villasDiscounts = EmptyArray,
    hideChildPrices,
    markCheapestPrice,
    tier,
    canLoyaltyUpgrade,
  } = props

  const dispatch = useOfferPageDispatch()
  const intl = useIntl()

  const defaultSoldOutText = intl.formatMessage({
    id: 'RLLnBLk9It',
    defaultMessage: 'Sold out',
    description: 'Default sold out text for calendar day cell',
  })
  const resolvedSoldOutText = soldOutText ?? defaultSoldOutText

  const checkInLabel = intl.formatMessage({
    id: 'KlDtstL1CZ',
    defaultMessage: 'Check in',
    description: 'Label shown on calendar day cell when it is the check-in date',
  })
  const checkOutLabel = intl.formatMessage({
    id: '+zhAvUGisj',
    defaultMessage: 'Check out',
    description: 'Label shown on calendar day cell when it is the check-out date',
  })
  const selectCheckInLabel = intl.formatMessage({
    id: 'ct1+O+2qIA',
    defaultMessage: 'Select check-in date',
    description: 'Label to select check-in date when child prices are hidden in calendar day cell',
  })
  const viewAvailabilityLabel = intl.formatMessage({
    id: 'DXxQaKAck/',
    defaultMessage: 'View availability',
    description: 'Label to view availability when child prices are hidden and check-in is selected in calendar day cell',
  })

  const isFromPrice = !isRoomSelected
  const flightPriceAvailable = !!day.pricingWithFlights?.price
  const isPriceWithFlights = !!(flights && flightPriceAvailable)
  const price = (displayMemberPrice && day.memberPricing) ?
      (isPriceWithFlights ? day.memberPricingWithFlights!.price : day.memberPricing.price) :
      (isPriceWithFlights ? day.pricingWithFlights!.price : day.pricing.price)
  const saleUnit = 'total'
  const available = day.hotelAvailable && (!flights || day.flightsAvailable) && (flightsOnly ? day.flightsAvailable : true)
  const canSwitchToHotel = day.hotelAvailable && flights && !flightsOnly
  const colour = (isCheckIn || isCheckOut) ? 'neutral-one' : undefined
  const showLuxPlus = displayMemberPrice && day.memberPricing && !(isCheckIn || isCheckOut)

  const colorString = useMemo<ComponentProps<typeof BodyText>['colour']>(() => {
    if (hasPromo) {
      return 'highlight-secondary'
    } else if (showLuxPlus) {
      return 'lux-plus'
    } else {
      return markCheapestPrice && !colour && day.cheapest ? 'highlight-secondary' : colour
    }
  },
  [hasPromo, showLuxPlus, colour, day.cheapest, markCheapestPrice])

  const availability = useMemo(() => {
    const result = {
      inOtherRoomTypes: false,
      showRecommendations: false,
      available: false,
      soldOut: false,
      canSwitchHotel: false,
    }
    if (available) {
      result.available = true
    } else if (!available) {
      if (canSwitchToHotel) {
        result.canSwitchHotel = true
      } else if (hasOtherAvailability) {
        result.inOtherRoomTypes = true
      } else {
        if (enableCalendarRecommendations) {
          result.showRecommendations = true
        } else {
          result.soldOut = true
        }
      }
    }
    return result
  }, [available, canSwitchToHotel, hasOtherAvailability, enableCalendarRecommendations])

  const isInvalidBundleDate = !isComplementaryCheckOut && isIllegalBundleSelection

  const display = useMemo(() => {
    const cellSelection = {
      isBetweenDay,
      isCheckOut,
      isCheckIn,
      isNone: !isBetweenDay && !isCheckIn && !isCheckOut,
    }

    return {
      cellSelection,
      shouldDisableButton: availability.soldOut || (availability.showRecommendations && !cellSelection.isNone),
      isUnselectable: isInvalidBundleDate,
      shouldHideMainInformation: cellSelection.isBetweenDay || cellSelection.isCheckOut || (isComplementaryBundleCell && !isComplementaryCheckOut),
      showSwitchToViewHotel: availability.canSwitchHotel,
      showAvailableInOtherRoomTypes: availability.inOtherRoomTypes,
      showSoldOut: availability.showRecommendations || availability.soldOut,
      showRecommendationLink: availability.showRecommendations && cellSelection.isNone && !bundleOfferId,
      showSoldOutBackground: availability.showRecommendations && cellSelection.isNone,
      showPrices: availability.available && (!flightsOnly || (flightsOnly && day.flightCacheActive)),
      showUrgencyLabel: showUrgencyLabels && isRoomSelected && !isBetweenDay && !isCheckIn && !isCheckOut,
      isBundleSelection: !!bundleOfferId,
      complementaryCell: isComplementaryBundleCell,
      complementaryCheckIn: isComplementaryCheckIn,
      complementaryCheckOut: isComplementaryCheckOut,
      shouldWhitenDay: isComplementaryCheckIn || isComplementaryCheckOut,
      complementaryCheckInIntersect: isComplementaryCheckIn && isCheckOut,
      complementaryCheckOutIntersect: isComplementaryCheckOut && isCheckIn,
      inversePallete: isComplementaryBundleCell && !(isComplementaryCheckIn || isComplementaryCheckOut),
      showPropertyName: isComplementaryCheckIn,
    }
  }, [isBetweenDay, isCheckOut, isCheckIn, availability.soldOut, availability.showRecommendations, availability.canSwitchHotel, availability.inOtherRoomTypes, availability.available, isInvalidBundleDate, isComplementaryBundleCell, isComplementaryCheckOut, bundleOfferId, flightsOnly, day.flightCacheActive, showUrgencyLabels, isRoomSelected, isComplementaryCheckIn])

  const showRoomsLabel = !display.showSoldOut && display.showUrgencyLabel && !(display.complementaryCell && !display.complementaryCheckOut)

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    if (isInvalidBundleDate) {
      onInvalidBundleDateClick()
      return
    }
    if (availability.canSwitchHotel) {
      dispatch({ type: OfferPageActions.setFlights, flights: false })
    } else if (availability.inOtherRoomTypes) {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
      dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
    } else if (availability.showRecommendations) {
      dispatch({ type: OfferPageActions.showRecommendationsView, checkInDay: day.checkIn })
    } else {
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
      dispatch({ type: OfferPageActions.setLastInteractedCalendarDay, lastInteractedCalendarDay: day })
    }
    onClick?.(day)
  }, [isCheckIn, isInvalidBundleDate, availability.canSwitchHotel, availability.inOtherRoomTypes, availability.showRecommendations, onClick, day, onInvalidBundleDateClick, dispatch, currentOfferId])

  let bundleAdditionalAmount = 0
  if (cheapestDay?.pricing) {
    const diff = day.pricing.price - cheapestDay.pricing.price
    if (diff >= 1) {
      bundleAdditionalAmount = diff
    }
  } else if (day.surcharge > 0) {
    bundleAdditionalAmount = day.surcharge
  }

  let FireIcon: React.ReactElement
  if (isLPCTacticalEnabled()) {
    FireIcon = <SolidFireIcon size="XS" colour={!colour ? 'urgency' : undefined} />
  } else {
    FireIcon = <LineFireIcon size="XS" colour={!colour ? 'highlight-secondary' : undefined} />
  }

  const isDateInVillasDiscountPeriod = villasDiscounts &&
    villasDiscounts.length > 0 &&
    isDateWithinAnyDiscountPeriod(villasDiscounts, moment(day.checkIn), duration)

  const dayIcon = useMemo(() => {
    if (day.hasFlightDeal && flights) {
      return <SolidFireIcon size="XS" colour="urgency" />
    } else if ((!!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package) && canLoyaltyUpgrade) {
      return <SolidCircleIcon size="XS" colour={`tier-${tier}`} />
    }
  }, [day, flights, tier, canLoyaltyUpgrade])

  return (
    <CellWrapper
      style={style}
      data-testid={clsx({
        'available-day-cell': availability.available,
      })}
      className={clsx(className, getThemeClassName({ mobile: display.inversePallete && !display.isBundleSelection ? 'inverse' : 'default' }), {
        unselectable: display.showAvailableInOtherRoomTypes || display.isUnselectable,
        'check-in': display.cellSelection.isCheckIn,
        'check-out': display.cellSelection.isCheckOut,
        'between-day': display.cellSelection.isBetweenDay,
        'sold-out-background': display.showSoldOutBackground,
        'is-complementary': display.complementaryCell,
        'complementary-check-in': display.complementaryCheckIn,
        'complementary-check-out': display.complementaryCheckOut,
        'is-bundle': display.isBundleSelection,
      })}
      disabled={display.shouldDisableButton}
      onClick={onCellClick}
    >
      <HeaderGroup
        fullWidth
        direction="horizontal"
        verticalAlign="space-evenly"
        horizontalAlign="space-between"
        data-testid={clsx({ [`d-${day.checkIn}`]: availability.available })}
      >
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText
            colour={display.shouldWhitenDay ? 'neutral-eight' : colour}
            variant="medium"
          >
            {day.day}
          </BodyText>
          {dayIcon}
        </Group>
        <Group direction="horizontal" verticalAlign="center" gap={4}>
          {(display.cellSelection.isCheckIn || display.cellSelection.isCheckOut) && <BodyText colour="neutral-one" as="span" variant="small" weight="bold">
            {display.cellSelection.isCheckIn && checkInLabel}
            {display.cellSelection.isCheckOut && checkOutLabel}
          </BodyText>}
          {showRoomsLabel && <RoomsRemainingLabel roomCount={day.availableRooms} kind="plain" />}
          {hasPromo && !display.cellSelection.isBetweenDay && <SolidUsdCircleIcon size="XS" colour={!colour ? 'highlight-secondary' : undefined} />}
          {!hasPromo && display.cellSelection.isNone && isTacticalPromotion && FireIcon}
          {isDateInVillasDiscountPeriod && !display.showSoldOut && FireIcon}
        </Group>
      </HeaderGroup>

      {display.shouldHideMainInformation && display.showPropertyName && complementaryPropertyName &&
        <PropertyNameContainer>
          <BodyText colour="neutral-eight" lineClamp={2} underline variant="small" weight="bold">{complementaryPropertyName}</BodyText>
        </PropertyNameContainer>}

      {!display.shouldHideMainInformation && <Main data-testid="d-main" className={clsx({ 'align-items-end': display.isBundleSelection })}>
        {display.showPrices && <>
          {bundleOfferId && (
            <BundleAndSaveAdditionalAmount data-testid="day-price">
              <BodyText colour={colour} variant="small" weight="normal">
                {pluralizeToString('night', duration)}
              </BodyText>
              {bundleAdditionalAmount > 0 && (
                <BodyText colour={colour} variant="small" weight="normal">
                  from +<FormatCurrency value={bundleAdditionalAmount} />*
                </BodyText>
              )}
            </BundleAndSaveAdditionalAmount>
          )}
          {!bundleOfferId && (<>
            {hideChildPrices && <Caption variant="large" colour={colour ?? 'neutral-three'} underline>
              {display.cellSelection.isNone && selectCheckInLabel}
              {display.cellSelection.isCheckIn && viewAvailabilityLabel}
            </Caption>}
            {/* TODO: i18n - complex pricing with leading/trailing spaces (' + flights', ' from', ' {saleUnit}', ' + flights') */}
            {!hideChildPrices && <>
              <Caption colour={colour ?? 'neutral-three'} variant="medium">
                {pluralizeToString('night', duration)}
                {flights && flightPriceAvailable && ' + flights'}
                {(isFromPrice || (flights && !flightPriceAvailable)) && ' from'}
              </Caption>
              <div data-testid="day-price">
                <BodyText colour={colorString} as="span" variant="medium" weight="bold">
                  {' '}
                  <FormatCurrency value={price} />
                </BodyText>
                <Caption colour={showLuxPlus ? 'lux-plus' : colour} as="span" variant="medium"> {saleUnit}{flights && '*'}</Caption>
                {flights && !flightPriceAvailable && <Caption colour={colour} as="span" variant="medium"> + flights</Caption>}
              </div>
            </>}
          </>)}
        </>}

        {display.showSwitchToViewHotel && <>
          <Caption variant="large" colour={colour ?? 'neutral-three'}>
            {intl.formatMessage({
              id: '9YXkp23kar',
              defaultMessage: 'Flights not available.',
              description: 'Message shown when flights are not available for calendar day cell',
            })}
          </Caption>
          <TextLink size="small" weight="regular">
            {intl.formatMessage({
              id: 'w3miWxKIL3',
              defaultMessage: 'View hotel prices',
              description: 'Link text to view hotel-only prices when flights are not available',
            })}
          </TextLink>
        </>}

        {display.showSoldOut && <Caption variant="large" colour={colour ?? 'neutral-three'}>{resolvedSoldOutText}</Caption>}
        {display.showRecommendationLink && <TextLink size="small" weight="regular">
          {intl.formatMessage({
            id: 'os5Oyb/wcz',
            defaultMessage: 'View similar offers',
            description: 'Link text to view similar offers when a month is sold out in calendar month picker',
          })}
        </TextLink>}

        {display.showAvailableInOtherRoomTypes && (<>
          <Caption variant="large" colour="neutral-three">
            {intl.formatMessage({
              id: '9OXkw9K+qs',
              defaultMessage: 'Room unavailable',
              description: 'Message shown when room is unavailable for calendar day cell',
            })}
          </Caption>
          <TextLink size="small" weight="regular">
            {intl.formatMessage({
              id: 'D/aQPV/ArY',
              defaultMessage: 'View room options',
              description: 'Link text to view available room options',
            })}
          </TextLink>
        </>)}
      </Main>}
    </CellWrapper>
  )
}

export default React.memo(OfferCalendarDayCell)
